import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Eye } from 'lucide-react';
import { projectsAPI } from '../utils/api';
import ProjectCard from '../components/ProjectCard';
import resumeFile from '../assets/Debojit Resume.pdf';

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProjects();
  }, []);

  const loadFeaturedProjects = async () => {
    try {
      const response = await projectsAPI.getAll({ featured: true });
      setFeaturedProjects(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading featured projects:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative min-h-screen flex items-start md:items-center justify-center px-6 pt-28 sm:pt-32 md:pt-0 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="absolute inset-0 hero-glow"></div>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1746563947316-6f4cc65a66fa?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center mt-8 md:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold font-['Syne'] tracking-tighter leading-[0.92] sm:leading-[0.9] mb-6" 
              data-testid="hero-title"
            >
              <motion.span 
                className="text-white/60 inline-block text-4xl sm:text-5xl md:text-7xl lg:text-8xl"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Hi, I'm
              </motion.span>{' '}
              <motion.span 
                className="inline-block text-4xl sm:text-5xl md:text-7xl lg:text-8xl bg-gradient-to-r from-white via-white to-primary bg-clip-text text-transparent animate-gradient-x"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 100 }}
              >
                Debojit
              </motion.span>
              <motion.span 
                className="text-primary block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold leading-[1.05] mt-3 sm:mt-2 drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Graphics Designer & Developer
              </motion.span>
            </motion.h1>
            <motion.p 
              className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-12" 
              data-testid="hero-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Passionate about crafting exceptional digital experiences. I blend creativity with strategy to transform ideas into stunning visual solutions that captivate and inspire.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <motion.div
                whileHover={{ scale: 1.05, rotate: 1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/work"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
                  data-testid="view-work-button"
                >
                  <Eye size={20} />
                  View Work
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/about"
                  className="border border-white/20 bg-transparent hover:bg-white/5 text-white rounded-full px-8 py-6 text-lg transition-all flex items-center gap-2 group"
                  data-testid="get-in-touch-button"
                >
                  About Me
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ArrowRight size={20} />
                  </motion.span>
                </Link>
              </motion.div>
            </motion.div>
            <motion.div
              className="mt-4 flex items-center justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
            >
              <motion.a
                href={resumeFile}
                download="Debojit Resume.pdf"
                className="border border-primary/60 bg-primary/10 hover:bg-primary/20 text-white rounded-full px-8 py-4 text-base font-medium transition-all inline-flex items-center gap-2"
                data-testid="download-resume-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Download size={18} />
                Download Resume
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {featuredProjects.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="featured-projects-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-bold font-['Syne'] tracking-tight mb-4" data-testid="featured-projects-title">
                Featured Work
              </h2>
              <p className="text-base md:text-lg text-muted-foreground">
                A selection of my best projects
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {featuredProjects.map((project, index) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  size={index === 0 ? 'large' : 'standard'}
                />
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/work"
                className="inline-flex items-center gap-2 text-primary hover:gap-4 transition-all font-medium"
                data-testid="view-all-projects-link"
              >
                View All Projects
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;